import React from "react";
import { ReportHeader } from "../external/ensolvers-core-frontend-mui/dto/ReportDTO";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { ListItemText } from "@material-ui/core";
import { ColorUtils } from "../utils/ColorUtils";

interface Props<T> {
  /**
   * Table headers configuration
   */
  headers: ReportHeader[];
  /**
   * Data to be rendered
   */
  rows: T[];
  /**
   * Function used to render the content of the table (body).
   * Must map an object instance (element in prop `rows`) to a ReactElement
   */
  tableRow?: (instance: T) => React.ReactElement;

  /**
   * Triggers an event when clicking on a row if this function is provided.
   * Also, the cursor becomes a pointer on row hover
   */
  onRowClick?: (instance: T) => void;

  /**
   * If `true`, the size of the table will be "small"
   */
  denseTable?: boolean;

  /**
   * If this function is provided, it will be used to render the content of the table cells for each header.
   *
   */
  renderCustomCell?: (header: ReportHeader, index: number) => React.ReactElement;

  /**
   * GenericTable prop
   * If true, the table header will stick to the top as the user scrolls down the table
   */
  stickyHeader?: boolean;

  /**
   *  GenericTable prop
   *  The maximum height of the table container.
   *  If the table exceeds this height, it will become scrollable
   */
  maxHeight?: string;

    /**
   * If this function is provided, it will be used to render a checkbox in the first position of the header,
   * usually this is used for a "Select all" feature.
   */
  renderSelectAllHeader?: (header: ReportHeader) => React.ReactElement;
}



export default function ReportTable<T>({
  headers,
  rows,
  tableRow,
  onRowClick,
  denseTable,
  renderCustomCell,
  maxHeight,
  stickyHeader,
  renderSelectAllHeader
}: Props<T>) {

  return (
    <TableContainer style={{ maxHeight: stickyHeader ? maxHeight || "65vh" : "", border: `1px solid ${ColorUtils.LIGHT_GRAY}`, boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)" }}>
      <Table stickyHeader={stickyHeader} sx={{ minWidth: 650 }} aria-label="simple table" size={denseTable ? "small" : undefined}>
        <TableHead>
          <TableRow>
                
            {headers?.map((header, index) =>
              renderCustomCell ?
               index === 0 && renderSelectAllHeader ?
                renderSelectAllHeader(header) :                
                renderCustomCell(header, index) :
                (
                <TableCell
                  key={`h-${index}`}
                  align={header.align}
                  width={header.width}
                  style={{
                    fontWeight: "bold",
                  }}
                >
                  {header.label}
                </TableCell>
              )
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableRow && rows.length ? (
            rows.map((row, index) => (
              <TableRow key={`row-${index}`} onClick={() => onRowClick && onRowClick(row)}>
                {tableRow!(row)}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell>
                <ListItemText primary="No data to display" />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
